/** MUI */
import {
  Avatar,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {useSelector} from "react-redux";
import {RootState} from "../../store/store";
import {examDetails} from "../../config/exam";
import {useGetExamPageSection} from "../../pages/exam/common/navigation/useGetExamPageSection";
import {ExamPageSection} from "../../pages/exam/common/navigation/routes";
import {useExamPageNavigate} from "../../pages/exam/common/navigation/useExamPageNavigate";
import BrandAvatarBox from "../../components/top-bar/brand-avatar-box";
import LoginSignupStack from "./components/LoginSignupStack";
import {useGetProfileQuery} from "../../store/api/userApiSlice";
import {PAGE_GRID_RATIO} from "../../config/page-grid-ratio";

type NavbarItemType = {
  name: string;
  type: ExamPageSection;
  isDisabled?: boolean;
};

const navItems: NavbarItemType[] = [
  {name: "Practice", type: ExamPageSection.PROBLEM_SET},
  {name: "Mock Test", type: ExamPageSection.MOCK_TEST_EXPLORE},
  {name: "Planner", type: ExamPageSection.PLANNER, isDisabled: true},
];

export default function Navbar() {
  const currentSection = useGetExamPageSection();
  const examPageNavigate = useExamPageNavigate();

  const {data, isLoading} = useGetProfileQuery();
  const examType = useSelector((state: RootState) => state.examSession.exam);

  const isLoggedIn = !!data;

  const handleNavItemClick = (type: ExamPageSection) => {
    if (
      [
        ExamPageSection.MOCK_TEST_EXPLORE,
        ExamPageSection.PLANNER,
        ExamPageSection.PROBLEM_SET,
      ].includes(type)
    ) {
      examPageNavigate(type, undefined);
    }
  };

  return (
    <Grid container direction="row" justifyContent="center">
      <Grid size={PAGE_GRID_RATIO.leftMargin} />
      <Grid size={PAGE_GRID_RATIO.mainContent}>
        <Stack direction="row" spacing="20px" sx={{height: "56px"}}>
          <BrandAvatarBox />
          <Paper
            variant="feature"
            sx={{
              width: "100%",
              p: "8px",
            }}>
            <List
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "fit-content",
                height: "100%",
                p: 0,
                gap: "8px",
              }}>
              {navItems.map((item, idx) => (
                <ListItemButton
                  key={idx}
                  selected={item.type === currentSection}
                  onClick={() => handleNavItemClick(item.type)}
                  sx={{
                    borderRadius: "12px",
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: "primary.dark",
                      "&:hover": {
                        bgcolor: "primary.main",
                        color: "primary.dark",
                        cursor: "default",
                      },
                    },
                  }}
                  disabled={item.isDisabled}>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2">{item.name}</Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Stack>
      </Grid>
      <Grid size={PAGE_GRID_RATIO.middleMargin} />
      <Grid size={PAGE_GRID_RATIO.rightSidebar}>
        <Stack direction="row" sx={{height: "56px"}}>
          <Paper
            variant="feature"
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              px: "8px",
            }}>
            <Stack
              justifyContent="center"
              textAlign={"start"}
              alignItems={"start"}
              ml="12px">
              <Typography
                variant="subtitle1"
                fontWeight={600}
                lineHeight={"1rem"}
                fontSize={"0.875rem"}>
                {examType !== null ? examDetails[examType].header : "Exam Name"}
              </Typography>
              <Typography
                variant="subtitle2"
                fontSize={"0.75rem"}
                style={{color: "secondary.light", fontWeight: 400}}>
                {examType !== null
                  ? examDetails[examType].temporaryDescription
                  : "Exam Description"}
              </Typography>
            </Stack>
            <Stack direction={"row"} marginLeft={"auto"}>
              {isLoading ? (
                <></>
              ) : isLoggedIn ? (
                <Avatar
                  alt={data?.profile?.firstName}
                  src={data?.profile?.profilePicture}
                  sx={{":hover": {cursor: "pointer"}}}
                />
              ) : (
                <LoginSignupStack />
              )}
            </Stack>
          </Paper>
        </Stack>
      </Grid>
      <Grid size={PAGE_GRID_RATIO.rightMargin} />
    </Grid>
  );
}

export {LoginSignupStack};
